import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const Route = createFileRoute("/reset-password")({
  head: () => ({ meta: [{ title: "Reset password — Glow Long Golf" }] }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      // 1) Surface errors that Supabase put in the URL (expired/invalid link).
      const hash = new URLSearchParams(
        typeof window !== "undefined" ? window.location.hash.replace(/^#/, "") : "",
      );
      const query = new URLSearchParams(
        typeof window !== "undefined" ? window.location.search : "",
      );
      const urlError =
        hash.get("error_description") ||
        hash.get("error") ||
        query.get("error_description") ||
        query.get("error");
      if (urlError) {
        setError(decodeURIComponent(urlError.replace(/\+/g, " ")));
        return;
      }

      // 2) If we already have a session (e.g. detectSessionInUrl ran), we're good.
      const { data: sess } = await supabase.auth.getSession();
      if (sess.session) {
        if (!cancelled) setReady(true);
        return;
      }

      // 3) PKCE recovery: exchange ?code= for a session ourselves. This is
      // idempotent-guarded because the code is single-use; if it already ran
      // we'll fall through to the listener below.
      const code = query.get("code");
      if (code) {
        const { error: exErr } = await supabase.auth.exchangeCodeForSession(code);
        if (cancelled) return;
        if (exErr) {
          setError(
            "This reset link is invalid or has already been used. Please request a new one.",
          );
          return;
        }
        setReady(true);
        return;
      }

      // 4) Token-hash flow (legacy/email-link). Verify the OTP from the URL.
      const token_hash = query.get("token_hash") || hash.get("token_hash");
      const type = (query.get("type") || hash.get("type")) as
        | "recovery"
        | "email"
        | null;
      if (token_hash && type) {
        const { error: vErr } = await supabase.auth.verifyOtp({ token_hash, type });
        if (cancelled) return;
        if (vErr) {
          setError("This reset link is invalid or has expired. Please request a new one.");
          return;
        }
        setReady(true);
        return;
      }

      // 5) Nothing in the URL — give the auth listener ~3s to fire, then bail.
      const timer = setTimeout(() => {
        if (!cancelled && !ready) {
          setError(
            "We couldn't validate your reset link. Please request a new password reset email.",
          );
        }
      }, 3000);
      return () => clearTimeout(timer);
    }

    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        if (!cancelled) {
          setReady(true);
          setError(null);
        }
      }
    });

    init();

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    const { error: upErr } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (upErr) return toast.error(upErr.message);
    toast.success("Password updated");
    nav({ to: "/account" });
  }

  return (
    <section className="mx-auto max-w-md px-4 py-20 sm:px-6">
      <div className="rounded-3xl border border-border bg-card p-8 shadow-glow">
        <h1 className="font-display text-2xl font-bold">Set a new password</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {error
            ? "We hit a snag with your reset link."
            : ready
              ? "Choose a new password to finish signing in."
              : "Validating your reset link…"}
        </p>

        {error && (
          <div className="mt-5 space-y-3">
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
            <Link
              to="/auth"
              className="inline-flex h-11 w-full items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90"
            >
              Request a new reset link
            </Link>
          </div>
        )}

        {ready && !error && (
          <form onSubmit={submit} className="mt-6 space-y-3">
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password (8+ characters)"
              className="h-11 w-full rounded-lg border border-input bg-background px-4 text-sm"
            />
            <input
              type="password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              className="h-11 w-full rounded-lg border border-input bg-background px-4 text-sm"
            />
            <button
              type="submit"
              disabled={loading}
              className="h-11 w-full rounded-full bg-primary text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90 disabled:opacity-60"
            >
              {loading ? "…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
